import { useEffect, useState } from "react";
import { ListChecks, Plus, Trash2, GripVertical, Save, Info as InfoIcon } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { api } from "@/lib/api";
import { useSiteConfig } from "@/lib/siteConfig";
import { toast } from "sonner";

const FIELD_TYPES = [
  { value: "text", label: "Single-line text" },
  { value: "textarea", label: "Long text / paragraph" },
  { value: "number", label: "Number" },
  { value: "date", label: "Date" },
  { value: "yes_no", label: "Yes / No toggle" },
  { value: "dropdown", label: "Dropdown (pick one)" },
  { value: "info", label: "Info box (read-only note)" },
];

function uid() {
  return `f_${Math.random().toString(36).slice(2, 9)}`;
}

function FieldCard({ field, idx, total, onChange, onDelete, onMove }) {
  const updateOptions = (raw) => {
    const arr = raw.split("\n").map((s) => s.trim()).filter(Boolean);
    onChange({ ...field, options: arr });
  };

  return (
    <div className="bg-white border border-line rounded-sm p-5 space-y-4" data-testid={`field-card-${field.id}`}>
      <div className="flex items-center gap-3">
        <div className="flex flex-col">
          <button
            disabled={idx === 0}
            onClick={() => onMove(idx, idx - 1)}
            className="text-sage hover:text-forest disabled:opacity-30 text-xs leading-none"
            data-testid={`field-up-${field.id}`}
            aria-label="Move up"
          >▲</button>
          <button
            disabled={idx === total - 1}
            onClick={() => onMove(idx, idx + 1)}
            className="text-sage hover:text-forest disabled:opacity-30 text-xs leading-none mt-1"
            data-testid={`field-down-${field.id}`}
            aria-label="Move down"
          >▼</button>
        </div>
        <GripVertical className="w-4 h-4 text-sage" />
        <div className="flex-1">
          <Label className="text-xs uppercase tracking-[0.15em] text-clay font-semibold">
            Field {idx + 1} • {FIELD_TYPES.find((t) => t.value === field.type)?.label || field.type}
          </Label>
        </div>
        <Button
          variant="outline"
          onClick={() => onDelete(field.id)}
          className="border-danger/40 text-danger hover:bg-danger hover:text-bone rounded-sm h-9"
          data-testid={`field-delete-${field.id}`}
        >
          <Trash2 className="w-4 h-4" />
        </Button>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div>
          <Label className="text-forest">Label</Label>
          <Input
            value={field.label}
            onChange={(e) => onChange({ ...field, label: e.target.value })}
            placeholder="e.g. Specific doctors you see"
            className="mt-1.5 h-11 border-line input-clean"
            data-testid={`field-label-${field.id}`}
          />
        </div>
        <div>
          <Label className="text-forest">Type</Label>
          <Select value={field.type} onValueChange={(v) => onChange({ ...field, type: v })}>
            <SelectTrigger className="mt-1.5 h-11 border-line" data-testid={`field-type-${field.id}`}>
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {FIELD_TYPES.map((t) => (
                <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      </div>

      {field.type === "info" ? (
        <div>
          <Label className="text-forest">Info content</Label>
          <Textarea
            value={field.content || ""}
            onChange={(e) => onChange({ ...field, content: e.target.value })}
            rows={3}
            placeholder="Anything you want clients to read — disclaimers, instructions, fine print."
            className="mt-1.5 border-line input-clean resize-none"
            data-testid={`field-content-${field.id}`}
          />
        </div>
      ) : (
        <>
          <div>
            <Label className="text-forest">Helper text <span className="text-sage font-normal">(optional)</span></Label>
            <Input
              value={field.help || ""}
              onChange={(e) => onChange({ ...field, help: e.target.value })}
              placeholder="A short hint shown below the field."
              className="mt-1.5 h-11 border-line input-clean"
              data-testid={`field-help-${field.id}`}
            />
          </div>

          {field.type === "dropdown" && (
            <div>
              <Label className="text-forest">Dropdown options <span className="text-sage font-normal">(one per line)</span></Label>
              <Textarea
                value={(field.options || []).join("\n")}
                onChange={(e) => updateOptions(e.target.value)}
                rows={4}
                placeholder={"Term\nWhole\nFinal Expense\nIUL\nMortgage Protection"}
                className="mt-1.5 border-line input-clean resize-none font-mono text-sm"
                data-testid={`field-options-${field.id}`}
              />
            </div>
          )}

          <div className="flex items-center justify-between bg-cream/60 border border-line rounded-sm px-4 py-3">
            <Label className="text-forest">Required field</Label>
            <Switch
              checked={!!field.required}
              onCheckedChange={(v) => onChange({ ...field, required: v })}
              data-testid={`field-required-${field.id}`}
            />
          </div>
        </>
      )}
    </div>
  );
}

function FormEditor({ formKey, fields, setFields }) {
  const addField = (type = "text") => {
    setFields([
      ...fields,
      { id: uid(), label: "New field", type, required: false, options: [], help: "", content: "" },
    ]);
  };

  const updateField = (i, next) => {
    setFields(fields.map((f, idx) => (idx === i ? next : f)));
  };

  const deleteField = (id) => {
    if (!window.confirm("Delete this field? Existing client data referencing it will remain in the database.")) return;
    setFields(fields.filter((f) => f.id !== id));
  };

  const move = (from, to) => {
    if (to < 0 || to >= fields.length) return;
    const copy = [...fields];
    const [item] = copy.splice(from, 1);
    copy.splice(to, 0, item);
    setFields(copy);
  };

  return (
    <div className="space-y-4" data-testid={`form-editor-${formKey}`}>
      {fields.length === 0 && (
        <div className="bg-cream/60 border border-line rounded-sm p-8 text-center text-sage text-sm">
          No custom fields yet. Use the buttons below to add one.
        </div>
      )}

      {fields.map((field, i) => (
        <FieldCard
          key={field.id}
          field={field}
          idx={i}
          total={fields.length}
          onChange={(next) => updateField(i, next)}
          onDelete={deleteField}
          onMove={move}
        />
      ))}

      <div className="flex flex-wrap gap-2 pt-2">
        <Button onClick={() => addField("text")} variant="outline" className="border-dashed border-line text-sage hover:text-forest hover:border-forest rounded-sm h-10" data-testid={`add-text-${formKey}`}>
          <Plus className="w-4 h-4 mr-2" /> Text field
        </Button>
        <Button onClick={() => addField("textarea")} variant="outline" className="border-dashed border-line text-sage hover:text-forest hover:border-forest rounded-sm h-10" data-testid={`add-textarea-${formKey}`}>
          <Plus className="w-4 h-4 mr-2" /> Paragraph
        </Button>
        <Button onClick={() => addField("number")} variant="outline" className="border-dashed border-line text-sage hover:text-forest hover:border-forest rounded-sm h-10" data-testid={`add-number-${formKey}`}>
          <Plus className="w-4 h-4 mr-2" /> Number
        </Button>
        <Button onClick={() => addField("date")} variant="outline" className="border-dashed border-line text-sage hover:text-forest hover:border-forest rounded-sm h-10" data-testid={`add-date-${formKey}`}>
          <Plus className="w-4 h-4 mr-2" /> Date
        </Button>
        <Button onClick={() => addField("yes_no")} variant="outline" className="border-dashed border-line text-sage hover:text-forest hover:border-forest rounded-sm h-10" data-testid={`add-yesno-${formKey}`}>
          <Plus className="w-4 h-4 mr-2" /> Yes / No
        </Button>
        <Button onClick={() => addField("dropdown")} variant="outline" className="border-dashed border-line text-sage hover:text-forest hover:border-forest rounded-sm h-10" data-testid={`add-dropdown-${formKey}`}>
          <Plus className="w-4 h-4 mr-2" /> Dropdown
        </Button>
        <Button onClick={() => addField("info")} variant="outline" className="border-dashed border-line text-sage hover:text-forest hover:border-forest rounded-sm h-10" data-testid={`add-info-${formKey}`}>
          <InfoIcon className="w-4 h-4 mr-2" /> Info box
        </Button>
      </div>
    </div>
  );
}

export default function FormBuilder() {
  const { config, refresh } = useSiteConfig();
  const [health, setHealth] = useState(config.health_custom_fields || []);
  const [life, setLife] = useState(config.life_custom_fields || []);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    setHealth(config.health_custom_fields || []);
    setLife(config.life_custom_fields || []);
  }, [config.health_custom_fields, config.life_custom_fields]);

  const save = async () => {
    setSaving(true);
    try {
      await api.patch("/settings/site", {
        ...config,
        health_custom_fields: health,
        life_custom_fields: life,
      });
      await refresh();
      toast.success("Forms updated. Visitors see the changes immediately.");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Save failed");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div data-testid="form-builder">
      <div className="mb-6">
        <h2 className="font-heading text-2xl tracking-tight font-medium flex items-center gap-2">
          <ListChecks className="w-5 h-5 text-clay" /> Form builder
        </h2>
        <p className="text-sage mt-2 text-sm">
          Add, rename, reorder, or remove fields on your Health &amp; Life applications. These show up underneath the standard fields. <span className="font-medium text-forest">Info boxes</span> are useful for disclaimers or instructions visible to the applicant.
        </p>
      </div>

      <Tabs defaultValue="health" className="w-full">
        <TabsList className="bg-cream border border-line rounded-sm h-auto p-1 mb-6" data-testid="form-builder-tabs">
          <TabsTrigger
            value="health"
            className="data-[state=active]:bg-forest data-[state=active]:text-bone rounded-sm px-4 py-2"
            data-testid="form-builder-tab-health"
          >
            Health form ({health.length})
          </TabsTrigger>
          <TabsTrigger
            value="life"
            className="data-[state=active]:bg-forest data-[state=active]:text-bone rounded-sm px-4 py-2"
            data-testid="form-builder-tab-life"
          >
            Life form ({life.length})
          </TabsTrigger>
        </TabsList>

        <TabsContent value="health" className="mt-0">
          <FormEditor formKey="health" fields={health} setFields={setHealth} />
        </TabsContent>
        <TabsContent value="life" className="mt-0">
          <FormEditor formKey="life" fields={life} setFields={setLife} />
        </TabsContent>
      </Tabs>

      <div className="sticky bottom-4 mt-6 flex justify-end">
        <Button
          onClick={save}
          disabled={saving}
          className="bg-forest hover:bg-forestdark text-bone rounded-sm h-11 px-6 shadow-md"
          data-testid="form-builder-save-btn"
        >
          <Save className="w-4 h-4 mr-2" /> {saving ? "Saving…" : "Save form changes"}
        </Button>
      </div>
    </div>
  );
}
